import { MAX_PARALLEL_CHUNKED_UPLOADS } from "@/config/constants";

/**
 * Cross-tab upload slots via the Web Locks API.
 *
 * Each chunked upload session holds one of MAX_PARALLEL_CHUNKED_UPLOADS named
 * locks for its lifetime; the send gate counts held slots across ALL tabs of
 * the origin via locks.query(). Web Locks — not persisted state (zustand
 * persist / localStorage / IndexedDB) — because locks are owned by the tab
 * and AUTO-RELEASED by the browser when it dies (crash/close/reload). A
 * persisted counter outlives the process that wrote it: one crash with
 * uploads running and the user is locked out everywhere until manual
 * recovery. The browser already solves that lease problem; use it.
 *
 * Support: Chrome 69+, Firefox 96+, Safari/iOS 15.4+ — the same floor the
 * streaming feature already requires. Where unsupported (and in jsdom),
 * everything degrades to the per-tab registry gate (v1 behavior).
 */

const SLOT_PREFIX = "chunked-upload-slot-";

type LockManagerLike = {
  request: (
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: unknown | null) => Promise<void> | void,
  ) => Promise<unknown>;
  query?: () => Promise<{ held?: Array<{ name?: string }> }>;
};

function locksApi(): LockManagerLike | null {
  const nav = navigator as Navigator & { locks?: LockManagerLike };
  return nav.locks ?? null;
}

/**
 * Tries to acquire a free upload slot. Returns the release function, or null
 * when every slot is already held (across all tabs). Without Web Locks
 * support, returns a no-op release — the per-tab registry gate still applies.
 */
export async function acquireUploadSlot(): Promise<(() => void) | null> {
  const locks = locksApi();
  if (!locks) {
    return () => {};
  }

  for (let slot = 1; slot <= MAX_PARALLEL_CHUNKED_UPLOADS; slot++) {
    // Two promises per the Web Locks callback contract: `held` keeps the lock
    // callback pending (so the lock stays held) until releaseHeld() resolves
    // it; the awaited promise reports only whether this slot was free.
    let releaseHeld!: () => void;
    const held = new Promise<void>((r) => {
      releaseHeld = r;
    });
    const acquired = await new Promise<boolean>((resolve) => {
      locks
        .request(`${SLOT_PREFIX}${slot}`, { ifAvailable: true }, (lock) => {
          if (lock === null) {
            resolve(false);
            return;
          }
          resolve(true);
          return held; // hold the lock until releaseHeld() (or this tab dies)
        })
        .catch(() => resolve(false));
    });
    if (acquired) {
      return releaseHeld;
    }
  }
  return null;
}

/**
 * Cross-tab count of held upload slots. Null when Web Locks (or query) is
 * unavailable — the caller then falls back to the per-tab registry count.
 */
export async function heldUploadSlotCount(): Promise<number | null> {
  const locks = locksApi();
  if (!locks?.query) {
    return null;
  }
  try {
    const { held = [] } = await locks.query();
    return held.filter((l) => l.name?.startsWith(SLOT_PREFIX)).length;
  } catch {
    return null;
  }
}

// ── Single-blob-send liveness beacons ──────────────────────────────────────
//
// Unlike chunked uploads (which hold a CAPPED slot lock above), single-blob
// sends — documents / images / voice / edits / forward — are gated only by an
// in-memory, per-tab FIFO, so they leave NO cross-tab trace. A blob send that is
// live in another tab looks identical, to a cold-booting tab, to one whose tab
// died mid-send: both are still PENDING with `id === _clientId` and hold no lock.
// Without a beacon the stranded-send reconciliation would wrongly FAIL the live
// one. So each blob send holds a UNIQUELY-named lock for its duration — unique,
// so it never contends and never caps (it is a liveness marker, not a slot) — and
// the browser auto-releases it when the tab dies. Reconciliation counts held
// beacons and skips failing not-published rows while any is live.

const BLOB_SEND_PREFIX = "blob-send-live-";

/**
 * Acquire a cross-tab liveness beacon for one in-flight single-blob send.
 * Returns a release fn; where Web Locks is unsupported returns a no-op release
 * (the caller's per-tab fallback count then applies).
 */
export async function holdBlobSendLiveness(): Promise<() => void> {
  const locks = locksApi();
  if (!locks) {
    return () => {};
  }
  const name = `${BLOB_SEND_PREFIX}${crypto.randomUUID()}`;
  let releaseHeld!: () => void;
  const held = new Promise<void>((r) => {
    releaseHeld = r;
  });
  await new Promise<void>((resolve) => {
    locks
      .request(name, { ifAvailable: false }, () => {
        resolve();
        return held; // hold the beacon until releaseHeld() (or this tab dies)
      })
      .catch(() => resolve());
  });
  return releaseHeld;
}

/**
 * Cross-tab count of live single-blob sends (held beacons). Null when Web Locks
 * / query is unavailable — the caller falls back to the per-tab in-flight count.
 */
export async function heldBlobSendCount(): Promise<number | null> {
  const locks = locksApi();
  if (!locks?.query) {
    return null;
  }
  try {
    const { held = [] } = await locks.query();
    return held.filter((l) => l.name?.startsWith(BLOB_SEND_PREFIX)).length;
  } catch {
    return null;
  }
}
